/**
 * Camlin Companion — bridge page-context extractor (doc 116 §2.1.2, CO2).
 *
 * "Live page context (what the visitor sees right now): the bridge extracts
 * the current URL, title, headings, and a bounded visible-text digest, and
 * sends it with each turn (and on SPA navigation)."
 *
 * Split into two layers, following `chatTranscript.ts`'s convention:
 *
 *   - `extractPageContext` — pure, duck-typed against `PageContextSource`
 *     (NOT a real DOM `Document`), so it is fully unit-testable under this
 *     project's `tsx --test` runner without jsdom.
 *   - `watchPageContext` — the real DOM wiring (history monkey-patch,
 *     popstate, debounce) that calls `readPageContextSourceFromDocument`
 *     and `extractPageContext` on every SPA navigation. Untestable under
 *     `tsx --test` (no `window`); covered by source-string assertions in
 *     `pageContext.test.ts`, matching `useLiveListenControls.ts`'s
 *     established convention for this project.
 *
 * Privacy/safety posture (doc 116 §6): this digest is later rendered
 * server-side (`agentLoop.ts`'s `formatPageContextForModel`) as clearly
 * labeled UNTRUSTED data, never as an instruction — nothing here needs to
 * sanitize page text against injection, only to bound its size.
 */

import type { CompanionPageContext, CompanionPageStructure } from './types';

/** Headings budget — generous but bounded; the model-facing render applies its own (smaller) cap independently. */
export const DEFAULT_PAGE_CONTEXT_MAX_HEADINGS = 20;
/** Visible-text digest budget in characters, pre-truncation. */
export const DEFAULT_PAGE_CONTEXT_MAX_DIGEST_CHARS = 4000;
/** SPA navigations are coalesced within this window so a burst of pushState calls (common in router libraries) emits once. */
export const PAGE_CONTEXT_NAVIGATION_DEBOUNCE_MS = 250;

/**
 * Structured-digest caps (Companion v6) — MUST mirror the rendered
 * crawler's `STRUCTURE_CAPS` in
 * `apps/schema-service/src/companion/renderedCrawler.ts` so the live
 * capture and the crawl-time capture stay the same size and shape.
 */
export const PAGE_STRUCTURE_CAPS = {
  headings: 40,
  landmarks: 20,
  links: 60,
  buttons: 30,
  forms: 10,
  fieldsPerForm: 20,
  textPerItem: 120
} as const;

/** What the widget reports as `structure.renderer` — distinguishes live captures from the crawler's 'playwright-chromium'. */
export const PAGE_STRUCTURE_RENDERER = 'companion-live-bridge';

/** Minimal duck-typed surface `extractPageContext` needs — a real `Document` satisfies the shape callers build in `readPageContextSourceFromDocument`. */
export interface PageContextSource {
  url: string;
  title: string;
  headingTexts: string[];
  visibleText: string;
}

export function truncateVisibleText(text: string, maxChars: number): string {
  if (text.length <= maxChars) {
    return text;
  }
  if (maxChars <= 0) {
    return '';
  }
  return `${text.slice(0, maxChars - 1)}…`;
}

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Pure extraction: given a `PageContextSource` (already-read strings — no
 * DOM traversal here), produce a bounded `CompanionPageContext`.
 */
export function extractPageContext(
  source: PageContextSource,
  now: () => string = () => new Date().toISOString()
): CompanionPageContext {
  const headings = source.headingTexts
    .map((heading) => heading.trim())
    .filter((heading) => heading.length > 0)
    .slice(0, DEFAULT_PAGE_CONTEXT_MAX_HEADINGS);

  const visibleTextDigest = truncateVisibleText(
    collapseWhitespace(source.visibleText),
    DEFAULT_PAGE_CONTEXT_MAX_DIGEST_CHARS
  );

  return {
    url: source.url,
    title: source.title,
    headings,
    visibleTextDigest,
    capturedAt: now()
  };
}

/** Raw (uncapped) structural capture — what `readPageStructureFromDocument` collects before `capCompanionPageStructure` bounds it. */
export interface RawPageStructureCapture {
  headings: Array<{ level: number; text: string }>;
  landmarks: Array<{ role: string; label?: string }>;
  links: Array<{ text: string; href: string }>;
  buttons: Array<{ text: string }>;
  forms: Array<{
    name?: string;
    action?: string;
    fields: Array<{ label?: string; name?: string; type: string; required?: boolean; placeholder?: string }>;
    submitLabel?: string;
  }>;
  metaDescription?: string;
}

/**
 * Pure serialization-cap pass over a raw structure capture — the
 * widget-side twin of the crawler's `capPageStructure` (same caps, same
 * trim/dedupe rules), unit-testable without a DOM.
 */
export function capCompanionPageStructure(
  raw: RawPageStructureCapture,
  now: () => string = () => new Date().toISOString()
): CompanionPageStructure {
  const trim = (value: string) => value.replace(/\s+/g, ' ').trim().slice(0, PAGE_STRUCTURE_CAPS.textPerItem);

  const seenLinks = new Set<string>();
  const links: Array<{ text: string; href: string }> = [];
  for (const link of raw.links) {
    const text = trim(link.text ?? '');
    const href = (link.href ?? '').trim();
    if (!text || !href) continue;
    const key = `${text}|${href}`;
    if (seenLinks.has(key)) continue;
    seenLinks.add(key);
    links.push({ text, href });
    if (links.length >= PAGE_STRUCTURE_CAPS.links) break;
  }

  return {
    headings: raw.headings
      .filter((h) => h && typeof h.text === 'string' && h.text.trim().length > 0)
      .slice(0, PAGE_STRUCTURE_CAPS.headings)
      .map((h) => ({ level: h.level, text: trim(h.text) })),
    landmarks: raw.landmarks
      .slice(0, PAGE_STRUCTURE_CAPS.landmarks)
      .map((l) => ({ role: l.role, ...(l.label ? { label: trim(l.label) } : {}) })),
    links,
    buttons: raw.buttons
      .map((b) => trim(b.text ?? ''))
      .filter((text) => text.length > 0)
      .slice(0, PAGE_STRUCTURE_CAPS.buttons)
      .map((text) => ({ text })),
    forms: raw.forms.slice(0, PAGE_STRUCTURE_CAPS.forms).map((form) => ({
      ...(form.name ? { name: trim(form.name) } : {}),
      ...(form.action ? { action: form.action.trim().slice(0, 300) } : {}),
      fields: (form.fields ?? []).slice(0, PAGE_STRUCTURE_CAPS.fieldsPerForm).map((field) => ({
        ...(field.label ? { label: trim(field.label) } : {}),
        ...(field.name ? { name: trim(field.name) } : {}),
        type: field.type || 'text',
        ...(field.required ? { required: true } : {}),
        ...(field.placeholder ? { placeholder: trim(field.placeholder) } : {})
      })),
      ...(form.submitLabel ? { submitLabel: trim(form.submitLabel) } : {})
    })),
    ...(raw.metaDescription ? { metaDescription: trim(raw.metaDescription) } : {}),
    capturedAt: now(),
    renderer: PAGE_STRUCTURE_RENDERER
  };
}

const LANDMARK_SELECTOR =
  'header, nav, main, aside, footer, form, ' +
  '[role="banner"], [role="navigation"], [role="main"], [role="complementary"], ' +
  '[role="contentinfo"], [role="search"], [role="form"], [role="region"]';

const IMPLICIT_LANDMARK_ROLES: Record<string, string> = {
  header: 'banner',
  nav: 'navigation',
  main: 'main',
  aside: 'complementary',
  footer: 'contentinfo',
  form: 'form'
};

function accessibleLabel(el: Element): string | undefined {
  const aria = el.getAttribute('aria-label');
  if (aria && aria.trim()) return aria.trim();
  const labelledBy = el.getAttribute('aria-labelledby');
  if (labelledBy) {
    const target = el.ownerDocument?.getElementById(labelledBy.split(/\s+/)[0] ?? '');
    const text = target?.textContent?.trim();
    if (text) return text;
  }
  return undefined;
}

/** Layout-aware visibility test — `display:none` subtrees have no offsetParent (fixed-position elements pass via getClientRects). */
function isVisible(el: Element): boolean {
  const html = el as HTMLElement;
  if (typeof html.offsetParent !== 'undefined' && html.offsetParent !== null) return true;
  return typeof el.getClientRects === 'function' && el.getClientRects().length > 0;
}

function fieldLabel(doc: Document, field: Element): string | undefined {
  const aria = field.getAttribute('aria-label');
  if (aria && aria.trim()) return aria.trim();
  const id = field.getAttribute('id');
  if (id) {
    const label = doc.querySelector(`label[for="${id.replace(/"/g, '\\"')}"]`);
    const text = label?.textContent?.trim();
    if (text) return text;
  }
  const wrapping = field.closest('label');
  const text = wrapping?.textContent?.trim();
  return text || undefined;
}

/**
 * Reads the raw structural capture from the live DOM. Kept separate from
 * `capCompanionPageStructure` (pure, tested) following this module's
 * extract/wire split — this half needs a real `Document`.
 */
export function readPageStructureFromDocument(doc: Document = document): RawPageStructureCapture {
  const headings = Array.from(doc.querySelectorAll('h1, h2, h3, h4, h5, h6')).map((el) => ({
    level: Number(el.tagName.slice(1)) || 2,
    text: el.textContent || ''
  }));

  const landmarks = Array.from(doc.querySelectorAll(LANDMARK_SELECTOR)).map((el) => {
    const explicit = el.getAttribute('role');
    const role = explicit || IMPLICIT_LANDMARK_ROLES[el.tagName.toLowerCase()] || 'region';
    const label = accessibleLabel(el);
    return { role, ...(label ? { label } : {}) };
  });

  const links = Array.from(doc.querySelectorAll('a[href]'))
    .filter(isVisible)
    .map((el) => ({
      text: el.textContent || '',
      href: (el as HTMLAnchorElement).href || el.getAttribute('href') || ''
    }));

  const buttons = Array.from(
    doc.querySelectorAll('button, [role="button"], input[type="submit"], input[type="button"]')
  )
    .filter(isVisible)
    .map((el) => ({
      text:
        el.textContent?.trim()
        || (el as HTMLInputElement).value
        || el.getAttribute('aria-label')
        || ''
    }));

  const forms = Array.from(doc.querySelectorAll('form')).map((form) => {
    const fields = Array.from(form.querySelectorAll('input, select, textarea'))
      .filter((field) => (field.getAttribute('type') || '').toLowerCase() !== 'hidden')
      .map((field) => {
        const label = fieldLabel(doc, field);
        const name = field.getAttribute('name') || undefined;
        const type = field.tagName.toLowerCase() === 'input'
          ? (field.getAttribute('type') || 'text').toLowerCase()
          : field.tagName.toLowerCase();
        return {
          ...(label ? { label } : {}),
          ...(name ? { name } : {}),
          type,
          ...(field.hasAttribute('required') ? { required: true } : {}),
          ...(field.getAttribute('placeholder') ? { placeholder: field.getAttribute('placeholder') as string } : {})
        };
      });
    const submit = form.querySelector('button[type="submit"], input[type="submit"], button:not([type])');
    const submitLabel = submit
      ? (submit.textContent?.trim() || (submit as HTMLInputElement).value || undefined)
      : undefined;
    return {
      ...(form.getAttribute('name') || form.getAttribute('id')
        ? { name: (form.getAttribute('name') || form.getAttribute('id')) as string }
        : {}),
      ...(form.getAttribute('action') ? { action: form.getAttribute('action') as string } : {}),
      fields,
      ...(submitLabel ? { submitLabel } : {})
    };
  });

  const metaDescription =
    doc.querySelector('meta[name="description"]')?.getAttribute('content') || undefined;

  return { headings, landmarks, links, buttons, forms, ...(metaDescription ? { metaDescription } : {}) };
}

/**
 * One-shot capture of the full page context INCLUDING the structured
 * digest — what `CompanionWidget` seeds into the embed config at session
 * open (v6 item: the session's first turn must already carry the digest;
 * the mount-time `avatar:live:contextUpdate` dispatch fires before the
 * SDK's listener exists and was lost).
 */
export function capturePageContext(doc: Document = document): CompanionPageContext {
  const context = extractPageContext(readPageContextSourceFromDocument(doc));
  try {
    context.structure = capCompanionPageStructure(readPageStructureFromDocument(doc));
  } catch {
    // A structure-capture failure must never cost the basic digest.
  }
  return context;
}

/** True when `nextUrl` differs from the last-captured context's URL (or there is no previous capture yet). */
export function hasPageContextNavigated(
  previous: CompanionPageContext | null,
  nextUrl: string
): boolean {
  return previous === null || previous.url !== nextUrl;
}

const HEADING_SELECTOR = 'h1, h2, h3';

/**
 * Reads a `PageContextSource` from the live DOM. `document.body.innerText`
 * (not `textContent`) is used deliberately — `innerText` is layout-aware and
 * already excludes `display:none`/`visibility:hidden` content, which is the
 * closest built-in approximation of "what the visitor sees right now".
 */
export function readPageContextSourceFromDocument(doc: Document = document): PageContextSource {
  const headingTexts = Array.from(doc.querySelectorAll(HEADING_SELECTOR)).map(
    (el) => el.textContent || ''
  );
  const visibleText = doc.body ? doc.body.innerText || doc.body.textContent || '' : '';

  return {
    url: doc.location.href,
    title: doc.title,
    headingTexts,
    visibleText
  };
}

export interface PageContextWatcherHandle {
  stop: () => void;
  /** Force an immediate re-capture and emit (bypassing the debounce/URL-change check) — used for the initial capture. */
  captureNow: () => void;
}

export interface WatchPageContextOptions {
  onChange: (context: CompanionPageContext) => void;
  doc?: Document;
  win?: Window;
  debounceMs?: number;
}

/**
 * Wires SPA-navigation-aware page-context capture: an initial capture, then
 * a re-capture on every `popstate` and every `history.pushState`/
 * `replaceState` call (the two ways a client-side router changes the URL
 * without a full page load), debounced so a burst of router-internal calls
 * emits once. Restores the original `history` methods on `stop()`.
 */
export function watchPageContext(options: WatchPageContextOptions): PageContextWatcherHandle {
  const doc = options.doc ?? document;
  const win = options.win ?? window;
  const debounceMs = options.debounceMs ?? PAGE_CONTEXT_NAVIGATION_DEBOUNCE_MS;

  let lastEmitted: CompanionPageContext | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const captureAndMaybeEmit = () => {
    const nextUrl = doc.location.href;
    if (!hasPageContextNavigated(lastEmitted, nextUrl)) {
      return;
    }
    // Full capture (structure included, v6): SPA navigations re-send the
    // structured digest so dynamic pages the crawl missed stay covered.
    const context = capturePageContext(doc);
    lastEmitted = context;
    options.onChange(context);
  };

  const scheduleCapture = () => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(captureAndMaybeEmit, debounceMs);
  };

  const originalPushState = win.history.pushState.bind(win.history);
  const originalReplaceState = win.history.replaceState.bind(win.history);

  win.history.pushState = function patchedPushState(...args: Parameters<History['pushState']>) {
    const result = originalPushState(...args);
    scheduleCapture();
    return result;
  };
  win.history.replaceState = function patchedReplaceState(...args: Parameters<History['replaceState']>) {
    const result = originalReplaceState(...args);
    scheduleCapture();
    return result;
  };

  win.addEventListener('popstate', scheduleCapture);

  function stop(): void {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    win.removeEventListener('popstate', scheduleCapture);
    win.history.pushState = originalPushState;
    win.history.replaceState = originalReplaceState;
  }

  // Initial capture runs immediately (not debounced) — the first turn
  // should never wait 250ms for a page-context digest.
  captureAndMaybeEmit();

  return { stop, captureNow: captureAndMaybeEmit };
}
